import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ArrowRight, Plus } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { ParticipantAvatar } from '@/design';
import { ApiError, ProblemType, errorMessage } from '@/domains/api';
import { addSurveyRoute } from '@/domains/auth';
import { surveyApi } from '../api';
import type {
  IMyCycle,
  IMyCycleParticipant,
  IMyCyclePath,
  IMyParticipant,
  IMyTeamRouting,
  ParticipantKind
} from '../api/contracts';
import { teamRoutingPath } from '../paths';
import { surveyTitle } from '../survey-labels';
import { isRelayed, participantLabel, relationsOf } from './catalogue';

/**
 * The affordance that opens a draft, over `POST /api/v1/responses` — survey-api's own
 * route since SR-213 retired the dev one it stood in for.
 *
 * It lives apart from the surveys page because it is the half that carries decisions: the
 * cycle, the path and the participant a draft belongs to, plus the refusals each of those
 * can produce. The page is the list.
 *
 * **Whom it offers is survey-api's answer, per cycle** — `eligibleParticipants` (the teams
 * onboarding plan's Q2), less those the cycle already lists a response for. An archived team is
 * therefore never offered, and a survey only one of an account's participants is for asks
 * nobody which. Before, the account's whole participant list (`/my/participants`) was offered,
 * for every survey — everybody the account speaks for, whether the survey was for them or not.
 *
 * **A team is offered by its routing** (R8), which survey-api derives and this card only reads:
 *
 * - routed to a path by its answers or by Veritant — a start naming that path, which **sends no
 *   `pathCode`**: survey-api fills it, from the routing first and a prior response after (SR-416's
 *   `CreateResponseService`), and a code sent from here would overrule both;
 * - not routed yet — "Answer a few questions first", to the team's routing page, since a start
 *   would be refused `path_required`;
 * - routed by its firm alone (`source: 'firm'` — a wirehouse team with no decision held: migrated,
 *   registered before SR-416, or a staff decision cleared) — the same, the owner's Q4 (a): its
 *   routing page asks the private-wealth question, whose answer the start then proposes into
 *   `TEAM-RG-004`; started directly, the response would open with that answer blank and locked;
 * - routed to the RIA firm survey — said, with the way to register the firm;
 * - no routing at all (`teamRouting` null: an org survey-api does not count as a team yet, the
 *   Development seed's) — a button per path, as a multi-path survey always had, because that is
 *   what `POST /responses` then requires.
 *
 * **A participant the account reaches as a member or an assistant is named on its start** —
 * "… · Listed on <team>", "… · for <advisor>" (SR-487 ruling 12, the owner's Q15) — whether or not
 * the cycle offers anybody else: unnamed, the start would read as the account's own.
 *
 * **A team no active Teams cycle lists is told Veritant will contact it** (the owner's Q17): a
 * `listed` pin leaves out a team not on its access list, and `/my/cycles` then lists no Teams cycle
 * for it (PFR §6). The API says nothing more, so the note is inferred, and an archived team or
 * any team between two Teams cycles reads it too — accepted. Where nothing else is offered, it is
 * a card of its own.
 */

interface IStartSurveyCardProps {
  /** Active cycles with somebody left to offer (`isStartable`). The card renders nothing when
   *  this and `waiting` are both empty. */
  cycles: IMyCycle[];
  /** The account's teams no active Teams cycle lists (`IMyCatalogue.awaiting`). */
  waiting: IMyParticipant[];
  /** Whether "Register the firm" leads anywhere — `ADD_SURVEY_ROUTE` is an advisor's alone. */
  canAdd: boolean;
  /** The account's participants (`/my/participants`), read for their photos and relations: a
   *  cycle's `eligibleParticipants` carry neither. `null` while it is out, or for good when it
   *  failed — the menu then draws initials, no start says "for" or "Listed on", and whom it
   *  offers is unchanged. */
  participants: Nullable<IMyParticipant[]>;
  /** Handed the new response, or the existing one a `response-exists` conflict names. */
  onOpened: (responseId: string) => void;
  /** `null` clears the page's banner. Every attempt clears it before it starts, so a stale
   *  refusal never sits above a button that is already trying again. */
  onError: (message: Nullable<string>) => void;
}

export function StartSurveyCard({
  cycles,
  waiting,
  canAdd,
  participants,
  onOpened,
  onError
}: IStartSurveyCardProps) {
  const [starting, setStarting] = React.useState<Nullable<string>>(null);
  const photos = React.useMemo(
    () => new Map(participants?.map((held) => [held.participantId, held.photoUri])),
    [participants]
  );
  const relations = React.useMemo(() => relationsOf(participants), [participants]);
  /** A start's words for whom it opens: always for a member's or an assistant's participant,
   *  and for any other where the cycle offers more than one. */
  const whose = (participant: IMyCycleParticipant, named: boolean): string => {
    const relation = relations.get(participant.participantId);
    return named || isRelayed(relation)
      ? ` · ${participantLabel(participant.displayName, relation)}`
      : '';
  };

  const start = async (
    cycle: IMyCycle,
    participantId: string,
    key: string,
    path: IMyCyclePath | undefined
  ) => {
    // The busy trigger stays enabled so the menu it owns can hand focus back to it, which
    // means it can also be pressed again — this is what makes a second start impossible.
    // It is worth keeping: two starts in flight together do not both answer
    // `response-exists`. The loser of the race hits the unique index instead and is answered
    // `code-in-use` with `target: "code"` and no `responseId`, so the response it could have
    // been sent to is not in the document that refuses it.
    if (starting !== null) return;

    setStarting(key);
    onError(null);

    try {
      onOpened(await openResponse(cycle, participantId, path?.code));
    } catch (startError) {
      onError(startFailureMessage(startError, cycle));
    } finally {
      setStarting(null);
    }
  };

  const notes = waiting.map((team) => (
    <p key={team.participantId} role="note" data-teams-contact className="text-sm">
      <strong>{team.displayName}:</strong> {TEAMS_CONTACT}
    </p>
  ));

  if (cycles.length === 0) {
    if (notes.length === 0) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle>Teams survey</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2">{notes}</CardContent>
      </Card>
    );
  }

  const offered = cycles.map((cycle) => ({ cycle, offers: offersOf(cycle) }));

  const controls = ({ cycle, offers }: { cycle: IMyCycle; offers: Offer[] }) => {
    // A team's control names it only where the cycle offers more than one participant — the
    // same condition that makes a plain start ask which.
    const named = offers.length > 1;
    const choosing = offers
      .filter((offer) => offer.kind === 'choose')
      .map((offer) => offer.participant);

    // A multi-path survey cannot be created without a path code for a participant survey-api
    // routes nothing for, so each path is its own button rather than a choice made after the
    // fact.
    const chosen =
      choosing.length === 0
        ? []
        : (cycle.paths.length > 0 ? cycle.paths : [undefined]).map((path) => {
            const key = startKey(cycle, path);
            const busy = starting === key;

            return choosing.length > 1 ? (
              <ParticipantMenu
                key={key}
                subjectKind={cycle.survey.subjectKind}
                participants={choosing}
                photos={photos}
                relations={relations}
                label={startLabel(cycle, path)}
                busy={busy}
                // The busy one stays pressable: Radix restores focus to the trigger as the
                // menu closes, and a disabled button is not focusable, so disabling it here
                // would drop a keyboard respondent onto `<body>` mid-action. `start` guards
                // the re-entry that allows.
                disabled={starting !== null && !busy}
                onPick={(participantId) => void start(cycle, participantId, key, path)}
              />
            ) : (
              <Button
                key={key}
                variant="outline"
                disabled={starting !== null}
                onClick={() => void start(cycle, choosing[0].participantId, key, path)}
              >
                <Plus />
                {busy ? 'Opening…' : `${startLabel(cycle, path)}${whose(choosing[0], false)}`}
              </Button>
            );
          });

    const teams = offers.map((offer) => {
      const { participant } = offer;
      const team = whose(participant, named);
      const key = startKey(cycle, undefined, participant.participantId);

      if (offer.kind === 'routed') {
        return (
          <Button
            key={key}
            variant="outline"
            disabled={starting !== null}
            // No path: survey-api fills the routed one (the module's JSDoc says why).
            onClick={() => void start(cycle, participant.participantId, key, undefined)}
          >
            <Plus />
            {starting === key
              ? 'Opening…'
              : `${startLabel(cycle, routedPath(cycle, offer.routing))}${team}`}
          </Button>
        );
      }
      if (offer.kind === 'route') {
        return (
          <Button key={key} variant="outline" asChild>
            <Link to={teamRoutingPath(participant.participantId)}>
              <ArrowRight />
              {`${startLabel(cycle)}${team} · Answer a few questions first`}
            </Link>
          </Button>
        );
      }
      return null;
    });

    return [...chosen, ...teams];
  };

  const riaTeams = offered.flatMap(({ cycle, offers }) =>
    offers
      .filter((offer) => offer.kind === 'ria')
      .map(({ participant }) => (
        <RiaNotice
          key={`${cycle.surveyCycleId}|${participant.participantId}`}
          team={offers.length > 1 ? participant.displayName : null}
          canAdd={canAdd}
        />
      ))
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Open a survey</CardTitle>
        <CardDescription>
          Surveys you have not answered yet. Which surveys appear depends on what Veritant’s
          records hold about the participants you speak for.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap gap-3">{offered.flatMap(controls)}</div>
        {riaTeams}
        {notes}
      </CardContent>
    </Card>
  );
}

/**
 * The open Teams cycle a team is listed in, and its routing as that cycle carries it — for the
 * two screens that start from a team rather than from a cycle: its routing page, and
 * `/portal/surveys/add`'s "that number is already yours".
 *
 * Told by the routing, which survey-api sets on a Teams cycle's team alone, and never by the
 * order of the list: `/my/cycles` lists every survey the account's participants are for, and
 * the first active cycle naming a team need not be the Teams one — a start made there would be
 * a start of another survey, under the Teams survey's words.
 */
export function teamsCycleOf(
  cycles: readonly IMyCycle[],
  participantId: string
): { cycle: IMyCycle; routing: IMyTeamRouting } | undefined {
  for (const cycle of cycles) {
    if (cycle.phase !== 'active') continue;
    const held = cycle.eligibleParticipants.find(
      (candidate) => candidate.participantId === participantId
    );
    if (held?.teamRouting) return { cycle, routing: held.teamRouting };
  }
  return undefined;
}

/**
 * A team routed to the RIA firm survey: nothing of Teams is offered, and the way on is to
 * register the firm by its SEC number, a run of its own on `ADD_SURVEY_ROUTE` — the same words the
 * team's routing page says. `team` names it where the cycle offers more than one participant.
 */
export function RiaNotice({ team, canAdd }: { team: Nullable<string>; canAdd: boolean }) {
  return (
    <div role="note" className="grid gap-2 rounded-md border border-border p-3 text-sm">
      <p>
        <strong>{team ? `${team} takes` : 'This team takes'} the RIA firm survey</strong>, not
        the Teams survey. It is registered by the firm’s SEC number.
      </p>
      {canAdd && (
        <Button asChild variant="outline" className="justify-self-start">
          <Link to={addSurveyRoute('RIA_FIRM')}>Register the firm</Link>
        </Button>
      )}
    </div>
  );
}

/** What a team no active Teams cycle lists is told, and a Teams start refused `cycle-not-open`
 *  (Q17, Q23). */
export const TEAMS_CONTACT = 'Our team will contact you about the Teams survey.';

/** What the card offers one eligible participant — the module's JSDoc says each arm. */
type Offer =
  | { kind: 'choose'; participant: IMyCycleParticipant }
  | { kind: 'routed'; participant: IMyCycleParticipant; routing: IMyTeamRouting }
  | { kind: 'route'; participant: IMyCycleParticipant }
  | { kind: 'ria'; participant: IMyCycleParticipant };

/**
 * The participants that could still open a response in this cycle — those survey-api names
 * eligible and the cycle does not already list one for — each with what the card offers it.
 *
 * `/my/cycles` carries the responses of **every** participant the account acts for, so "has
 * anyone started this cycle" is a different question from "has this participant anything left
 * to start". survey-api's one-live-response rule is per `(participant, cycle)`, and two
 * participants of the same kind are each entitled to their own response in the same survey.
 * Offering the cycle only while its response list is empty retired it for the second
 * participant permanently, with no route back.
 *
 * An `archived` response counts as taken here even though it holds no live slot. Deciding which
 * statuses are live is survey-api's rule, not this app's, and guessing it here to widen the offer
 * would be the client-side evaluation this codebase refuses. A recycled response is not listed at
 * all (survey-api's recycle bin design D6), so a participant whose only response staff moved to
 * the bin is offered the cycle again — survey-api's live slot is free.
 */
function offersOf(cycle: IMyCycle): Offer[] {
  const taken = new Set(cycle.responses.map((response) => response.participantId));
  return cycle.eligibleParticipants
    .filter((participant) => !taken.has(participant.participantId))
    .map((participant): Offer => {
      const routing = participant.teamRouting;
      if (routing === null) return { kind: 'choose', participant };
      if (asksFirst(routing)) return { kind: 'route', participant };
      if (routing.outcome === 'ria') return { kind: 'ria', participant };
      return { kind: 'routed', participant, routing };
    });
}

/**
 * Whether a team answers its routing questions before its start: routed nowhere yet, or routed by
 * its firm alone, which survey-api's own read still asks the private-wealth question of (see the
 * module's JSDoc). Shared with "Add a survey"'s already-yours sentence, which links the same way.
 */
export function asksFirst(routing: IMyTeamRouting): boolean {
  return routing.outcome === null || routing.source === 'firm';
}

/** Whether the card offers this cycle anything at all. */
export function isStartable(cycle: IMyCycle): boolean {
  return offersOf(cycle).length > 0;
}

/** The path a routed team starts on, for its label — none where the routing names a path the
 *  cycle does not list: the label then says the survey alone, and survey-api still decides. */
export function routedPath(cycle: IMyCycle, routing: IMyTeamRouting): IMyCyclePath | undefined {
  return cycle.paths.find((path) => path.code === routing.pathCode);
}

/** Identifies one button in the card, and the one that is mid-flight: a cycle, and the path or
 *  the routed team the button is for. */
function startKey(cycle: IMyCycle, path?: IMyCyclePath, participantId?: string): string {
  return `${cycle.surveyCycleId}|${path?.code ?? ''}|${participantId ?? ''}`;
}

export function startLabel(cycle: IMyCycle, path?: IMyCyclePath): string {
  const survey = `${surveyTitle(cycle.survey.code)} ${cycle.cycle.code}`;
  return path ? `${survey} · ${path.name}` : survey;
}

/**
 * Opens a draft for `participantId` in `cycle`, answering the response to open — the new one,
 * or the existing one a `response-exists` conflict names. Anything else is thrown, for
 * `startFailureMessage`. Shared with the team's routing page, which starts the same way.
 */
export async function openResponse(
  cycle: IMyCycle,
  participantId: string,
  pathCode?: string
): Promise<string> {
  try {
    const created = await surveyApi.createResponse({
      surveyCycleId: cycle.surveyCycleId,
      participantId,
      pathCode
    });
    return created.responseId;
  } catch (startError) {
    // Exactly one live response per participant per cycle — so "already exists" is the
    // normal answer for a returning respondent, and the conflict carries the response to
    // continue. It can still happen with nothing listed on the page: another tab, or a
    // response belonging to a cycle this account cannot see.
    if (startError instanceof ApiError && startError.isProblem(ProblemType.responseExists)) {
      const existing = startError.problem?.responseId;
      if (existing) return existing;
    }
    throw startError;
  }
}

/**
 * Which participant a draft belongs to, asked only when the cycle offers more than one.
 *
 * The item **is** the choice: no preselection, no confirm step. A preselected subject would
 * be the same silent guess survey-api itself declines to make (it answers
 * `ambiguous_participant` rather than picking the one candidate whose kind matches), and the
 * cost of being wrong is asymmetric — an attestation signed as the wrong subject, holding a
 * live-response slot no route can free.
 *
 * `subjectKind` sorts and labels; it never hides. Filtering the list down to the matching
 * kind would recreate exactly the dead end this replaces, for a respondent whose migrated
 * kind is wrong or whose survey has no matching participant at all.
 */
function ParticipantMenu({
  subjectKind,
  participants,
  photos,
  relations,
  label,
  busy,
  disabled,
  onPick
}: {
  subjectKind: ParticipantKind;
  participants: IMyCycleParticipant[];
  photos: ReadonlyMap<string, Nullable<string>>;
  relations: ReadonlyMap<string, string>;
  label: string;
  busy: boolean;
  disabled: boolean;
  onPick: (participantId: string) => void;
}) {
  const matching = participants.filter((participant) => participant.kind === subjectKind);
  const others = participants.filter((participant) => participant.kind !== subjectKind);

  // Built as a list so an empty group drops out on its own: that is what keeps the
  // separator a between-groups rule rather than a third condition, and what makes the
  // second heading call anyone "other" only when there is a first group to be other than.
  const groups = [
    { heading: subjectLabel(subjectKind), members: matching },
    {
      heading: matching.length > 0 ? 'Other participants' : 'Your participants',
      members: others
    }
  ].filter((group) => group.members.length > 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <Plus />
          {busy ? 'Opening…' : label}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-72">
        {groups.map((group, index) => (
          <React.Fragment key={group.heading}>
            {index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel>{group.heading}</DropdownMenuLabel>
            {group.members.map((participant) => (
              <ParticipantItem
                key={participant.participantId}
                participant={participant}
                name={participantLabel(
                  participant.displayName,
                  relations.get(participant.participantId)
                )}
                photoUri={photos.get(participant.participantId) ?? null}
                onPick={onPick}
              />
            ))}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ParticipantItem({
  participant,
  name,
  photoUri,
  onPick
}: {
  participant: IMyCycleParticipant;
  /** The participant as the list names it (`participantLabel`). */
  name: string;
  photoUri: Nullable<string>;
  onPick: (participantId: string) => void;
}) {
  return (
    <DropdownMenuItem
      onSelect={() => onPick(participant.participantId)}
      // Radix types ahead over an item's `textContent` unless told otherwise, and `aria-hidden`
      // does not take the initials out of it — "(Dev) Wealth" would be read as "DW(Dev)…".
      textValue={participant.displayName}
      className="flex items-center justify-between gap-3"
    >
      <span className="flex min-w-0 items-center gap-2">
        <ParticipantAvatar
          photoUri={photoUri}
          displayName={participant.displayName}
          size="sm"
          decorative
        />
        <span className="truncate">{name}</span>
      </span>
      <span className="text-xs text-muted-foreground">{kindLabel(participant.kind)}</span>
    </DropdownMenuItem>
  );
}

/**
 * What the survey asks about, in a respondent's words. `subjectKind` is survey-api's
 * vocabulary — "org" on a menu means nothing to an advisor.
 *
 * Every member is spelled out and the fallback names none of them, following `statusBadge`
 * in `my-surveys-page.tsx` and for a sharper reason: a kind this app does not know would
 * otherwise be announced as one it does, and this is the screen where picking the wrong
 * legal subject cannot be undone — the response holds a slot no route frees.
 */
function subjectLabel(kind: ParticipantKind): string {
  switch (kind) {
    case 'person':
      return 'Answers about a person';
    case 'org':
      return 'Answers about a firm';
    case 'team':
      return 'Answers about a team';
    default:
      return 'Answers about this survey’s subject';
  }
}

/** The same vocabulary on a row rather than a heading. An unknown kind renders as itself,
 *  which is a label to look up rather than a wrong answer. */
function kindLabel(kind: ParticipantKind): string {
  switch (kind) {
    case 'person':
      return 'person';
    case 'org':
      return 'firm';
    case 'team':
      return 'team';
    default:
      return kind;
  }
}

/**
 * One sentence per refusal, because survey-api's own titles stand over more than one thing
 * each — and every one of them is a page gone stale: what the list offered is no longer what
 * survey-api holds.
 *
 * `invalid-action` is two of them under one title ("The action names things the response's
 * state does not offer"), so the page reads `problems[]` and says which happened:
 * `no_participant` — the account no longer acts for the participant the card named (the card
 * always names one, from `eligibleParticipants`, so the inferring arm of that refusal and
 * `ambiguous_participant` are unreachable from here) — and `path_required`, a team whose routing
 * was known when the list was read and is not now.
 *
 * The 409s are not `invalid-action` at all, and none is a mistake the respondent made.
 * `cycle-closed` (SR-213) is a statement about the cycle, so the message does not suggest
 * trying again — nothing the respondent does will reopen it. `code-in-use` is the losing half
 * of two starts racing: a response now exists and this document does not carry its id, so
 * re-reading the list is the only move. `participant-archived` and `team-routed-to-ria` are
 * Veritant's decisions about the team since the list was read; a reload shows what is left.
 * `cycle-not-open` (SR-487, PFR §6) is a `listed` pin's gate, which the list cannot see coming:
 * on a Teams cycle it says what a team no Teams cycle lists is told (Q17); on any other survey it
 * is the general refusal, since the Teams words would be about a survey not started (Q23).
 */
export function startFailureMessage(error: unknown, cycle: IMyCycle): string {
  if (error instanceof ApiError) {
    const survey = `${surveyTitle(cycle.survey.code)} ${cycle.cycle.code}`;

    if (error.isProblem(ProblemType.cycleNotOpen) && cycle.survey.code === 'TEAMS') {
      return TEAMS_CONTACT;
    }
    if (error.isProblem(ProblemType.cycleClosed)) {
      return `${survey} is closed, so nothing new can be started in it. Anything already filled in stays on this page.`;
    }
    if (error.isProblem(ProblemType.codeInUse)) {
      return `Another attempt to open ${survey} landed first. Reload the page — the response it opened is in your list.`;
    }
    if (error.isProblem(ProblemType.participantArchived)) {
      return `This team has been archived by Veritant, so ${survey} cannot be started for it. Reload the page to see what is left to start.`;
    }
    if (error.isProblem(ProblemType.teamRoutedToRia)) {
      return `This team is now routed to the RIA firm survey, so ${survey} cannot be started for it. Reload the page to see how to register the firm.`;
    }
    if (error.isProblem(ProblemType.invalidAction)) {
      const reasons = (error.problem?.problems ?? []).map((problem) => problem.reason);
      if (reasons.some((reason) => reason.startsWith('path_required'))) {
        return `Which part of ${survey} this team takes is no longer decided, so it cannot be started yet. Reload the page — it asks the questions that decide it.`;
      }
      if (reasons.some((reason) => reason.startsWith('no_participant'))) {
        return `This account no longer speaks for the participant ${survey} would be opened for. Reload the page and try again.`;
      }
    }
  }
  return errorMessage(error, 'Could not start that survey');
}
